import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { usePageState } from "../context/AppStateContext";
import { useAuth } from "../context/AuthContext";
import { normalizeRole } from "../utils/rolePermissions";
import {
  normalizeConditionScore,
  isConditionAcceptable,
} from "../utils/conditionScore";
// ✅ ADDED: same helper AddProductWizard uses to turn the AI's raw
// `analysis.metafields` object into the { key: value } shape formData
// expects. Reusing it (instead of re-implementing) keeps both pages
// behaving identically.
import { buildMetafieldsFromAnalysis } from "../components/AddProduct/helpers";
import "./SorterPage.css";
import AutoFillLoader from "../components/Common/Loaders/AutoFillLoader";
const API_HOST =
  process.env.REACT_APP_API_URL ||
  "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net";

// ─────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────
const API_BASE =
  "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api";

const DEMO_COMPANY_ID = "13";
const DEMO_LIMIT = 100;

// ✅ Local SVG placeholder — via.placeholder.com is dead (discontinued), was causing
// ERR_CONNECTION_CLOSED spam in console. This is a data-URI, no network call at all.
const PLACEHOLDER_IMG =
  "data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Crect width='120' height='120' fill='%23e0e0e0'/%3E%3Ctext x='50%25' y='50%25' font-size='13' text-anchor='middle' fill='%23999' dy='.3em'%3ENo Image%3C/text%3E%3C/svg%3E";

// Roles that: never see TO Price, never see Sell Online / Push to Retail /
// Wholesale actions, and can only edit `size` on an already-saved scan.
const RESTRICTED_ROLES = ["seller", "vendor"];

const AUTO_FILL_MESSAGES = [
  "Analyzing image...",
  "Fetching product details...",
  "Checking authentication...",
  "Assessing condition...",
  "Generating description...",
];

const UPLOAD_STATUS = {
  PENDING: "pending",
  UPLOADING: "uploading",
  SUCCESS: "success",
  FAILED: "failed",
};

const PAGE_STATE = {
  IDLE: "idle",
  LOADING: "loading",
  PROCESSING: "processing",
  SAVING: "saving",
  ERROR: "error",
};

const SCAN_STATUS_TABS = [
  { key: "", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "uploaded", label: "Uploaded" },
  { key: "rejected", label: "Rejected" },
];

const SCAN_STATUS_CONFIG = {
  pending: {
    label: "Pending",
    color: "#92400e",
    bg: "#fef3c7",
    border: "#fde68a",
  },
  uploaded: {
    label: "Uploaded",
    color: "#065f46",
    bg: "#d1fae5",
    border: "#6ee7b7",
  },
  rejected: {
    label: "Rejected",
    color: "#991b1b",
    bg: "#fee2e2",
    border: "#fca5a5",
  },
};

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────
const getThriftopsSuggestion = (authScore, conditionScore) => {
  const auth = parseFloat(authScore) || 0;
  const cond = normalizeConditionScore(conditionScore);
  if (auth >= 80 && cond >= 8) return "Sell Online";
  return "Wholesale";
};

// Pull a single "headline" price out of whatever the backend sent for
// this role, for the main "Potential Price" column. Owners / seller
// managers get both price_recommended and vendor_payout — headline
// prefers price_recommended for them. Editors only ever get
// price_recommended. Vendors/sellers only ever get vendor_payout.
const getHeadlinePrice = (pricing) => {
  if (!pricing) return null;
  if (
    pricing.price_recommended !== undefined &&
    pricing.price_recommended !== null
  ) {
    return pricing.price_recommended;
  }
  if (pricing.vendor_payout !== undefined && pricing.vendor_payout !== null) {
    return pricing.vendor_payout;
  }
  return null;
};

const mapToResultRow = (item, previewImage = null) => {
  const pricing = item.pricing || {};

  return {
    image: previewImage || (item.images?.length > 0 ? item.images[0] : null),
    description: item.title,
    price: getHeadlinePrice(pricing),
    priceRecommended: pricing.price_recommended ?? null,
    vendorPayout: pricing.vendor_payout ?? null,
    unit: item.currency || "",
    authScore: item.authenticity_score,
    conditionScore: normalizeConditionScore(item.condition_score),
    suggestion: getThriftopsSuggestion(
      item.authenticity_score,
      item.condition_score,
    ),
    authReasons: item.authenticity_summary
      ? [{ status: "pass", text: item.authenticity_summary }]
      : [],
    conditionReasons: item.condition_summary
      ? [{ status: "pass", text: item.condition_summary }]
      : [],
    status: item.status || "pending",
    rejectionReason: item.rejection_reason || null,
    createdAt: item.created_at || null,
    size: item.size || "",
    sku: item.sku || "",
    fleekDecision: item.fleek_decision || null,
    fleekListPrice: item.fleek_list_price ?? null,
    fleekCostFloor: item.fleek_cost_floor ?? null,
    fleekResellerCut: item.fleek_reseller_cut ?? null,
    fleekReason: item.fleek_reason || null,
    metafields: item.metafields || {},
    raw: item,
    needsRepair: !!item.needs_repair,
    needsWash: !!item.needs_wash,
    repairTypes: item.repair_types || [],
    careReason: item.care_reason || "",
  };
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// ─────────────────────────────────────────────
// STATUS BADGE COMPONENT
// ─────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const cfg = SCAN_STATUS_CONFIG[status] || {
    label: status || "Unknown",
    color: "#374151",
    bg: "#f3f4f6",
    border: "#d1d5db",
  };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: "20px",
        fontSize: "0.75rem",
        fontWeight: 600,
        color: cfg.color,
        background: cfg.bg,
        border: `1px solid ${cfg.border}`,
        whiteSpace: "nowrap",
      }}
    >
      {cfg.label}
    </span>
  );
};

// ─────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────
const SorterPage = () => {
  const [pageState, setPageState] = useState(PAGE_STATE.LOADING);
  const [images, setImages] = useState([]);
  const [results, setResults] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [autoFillMsgIndex, setAutoFillMsgIndex] = useState(0);
  const [processingError, setProcessingError] = useState(null);
  const [sellerList, setSellerList] = useState([]);
  const [selectedSellerId, setSelectedSellerId] = useState(null);

  // ── Filter state ──────────────────────────────────────────
  const [activeStatusTab, setActiveStatusTab] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [productSize, setProductSize] = useState("");
  const [sizeError, setSizeError] = useState(null);

  // ── Vendor: quick price-increase request — per-row loading flag.
  // No modal, no manual input: handleQuickPriceRequest fetches bounds
  // and submits the max allowed payout in one click. ─────────────
  const [submittingPriceRequestIds, setSubmittingPriceRequestIds] = useState(
    new Set(),
  );

  // ── Product settings (min images, file size) ──────────────
  const [productSettings, setProductSettings] = useState({
    minImages: 0,
    fileSizeKb: 5120,
  }); // eslint-disable-next-line
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // ── Active Shopify metafields (company-level definitions) ─
  const [activeMetafields, setActiveMetafields] = useState([]);
  // eslint-disable-next-line
  const [loadingMetafields, setLoadingMetafields] = useState(false);

  // ── Size-edit state (seller/vendor only) ──────────────────
  const [editingSizeId, setEditingSizeId] = useState(null);
  const [sizeDraft, setSizeDraft] = useState("");

  // ── Image reordering state ────────────────────────────────
  const [draggedImageId, setDraggedImageId] = useState(null);
  const [dragOverImageId, setDragOverImageId] = useState(null);

  // ── Image edit modal state ────────────────────────────────
  const [imageEditModalOpen, setImageEditModalOpen] = useState(false);
  const [editingResultId, setEditingResultId] = useState(null);
  const [editingImages, setEditingImages] = useState([]);
  const [imageEditSaving, setImageEditSaving] = useState(false);

  // ── Owner/admin/seller manager: price request REVIEW panel ────
  const [pendingPriceRequests, setPendingPriceRequests] = useState([]);
  const [priceRequestsLoading, setPriceRequestsLoading] = useState(false);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [reviewBreakdown, setReviewBreakdown] = useState(null);
  const [reviewBreakdownLoading, setReviewBreakdownLoading] = useState(false);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewError, setReviewError] = useState(null);

  const fileInputRef = useRef(null);
  const replaceInputRef = useRef(null);
  const replacingIdRef = useRef(null);
  const resultsRef = useRef(null);

  const { minimumAcceptableCondition, user, companyId } = useAuth();
  const { setState: setAddProductState } = usePageState("addProduct");
  const navigate = useNavigate();

  const company_id = companyId;
  const user_id = user?.id || user?.user_id || user?.userId;
  const userRole = normalizeRole(user?.role);
  const isEditor = userRole === "editor";
  const isRestricted = RESTRICTED_ROLES.includes(userRole);
  const canReviewPriceRequests = ["owner", "admin", "seller manager"].includes(
    userRole,
  );

  const isBusy =
    pageState === PAGE_STATE.PROCESSING || pageState === PAGE_STATE.SAVING;
  const hasImages = images.length > 0;
  const anyUploading = images.some(
    (img) => img.uploadStatus === UPLOAD_STATUS.UPLOADING,
  );
  const anyFailed = images.some(
    (img) => img.uploadStatus === UPLOAD_STATUS.FAILED,
  );
  const anyPending = images.some(
    (img) => img.uploadStatus === UPLOAD_STATUS.PENDING,
  );
  const trimmedSize = productSize.trim();
  const hasSize = trimmedSize.length > 0;

  const requireSize = () => {
    if (hasSize) return true;
    setSizeError("Please enter a size before uploading images.");
    return false;
  };

  const canProcess =
    hasImages &&
    hasSize &&
    !anyUploading &&
    !anyFailed &&
    !anyPending &&
    !isBusy;

  const activeUserId =
    isEditor && selectedSellerId ? selectedSellerId : user_id;

  const selectedSellerName = (() => {
    const seller = sellerList.find(
      (s) => String(s.user_id || s.id || s.userId) === String(selectedSellerId),
    );
    return seller?.storeName || seller?.store_name || seller?.name || "Seller";
  })();

  // ── Filtered results (frontend) ───────────────────────────
  const filteredResults = useMemo(() => {
    return results.filter((r) => {
      if (activeStatusTab && r.status !== activeStatusTab) return false;

      if (dateFrom || dateTo) {
        if (!r.createdAt) return false;
        const created = new Date(r.createdAt);
        if (isNaN(created)) return false;
        if (dateFrom) {
          const from = new Date(dateFrom);
          from.setHours(0, 0, 0, 0);
          if (created < from) return false;
        }
        if (dateTo) {
          const to = new Date(dateTo);
          to.setHours(23, 59, 59, 999);
          if (created > to) return false;
        }
      }

      return true;
    });
  }, [results, activeStatusTab, dateFrom, dateTo]);

  // ── Status counts for tab badges ─────────────────────────
  const statusCounts = useMemo(() => {
    const counts = { "": results.length, pending: 0, uploaded: 0, rejected: 0 };
    results.forEach((r) => {
      if (r.status in counts) counts[r.status]++;
    });
    return counts;
  }, [results]);

  // ── Auto-rotate loading messages ─────────────────────────
  useEffect(() => {
    if (pageState !== PAGE_STATE.PROCESSING) return;
    const interval = setInterval(() => {
      setAutoFillMsgIndex((prev) => (prev + 1) % AUTO_FILL_MESSAGES.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [pageState]);

  // ── Fetch sellers list (editor only) ─────────────────────
  useEffect(() => {
    if (!isEditor || !company_id) return;
    const fetchSellers = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/company-sellers/${company_id}`,
        );
        const sellers = res.data?.data || [];
        setSellerList(sellers);
        if (sellers.length > 0) {
          const firstId =
            sellers[0].user_id || sellers[0].id || sellers[0].userId;
          setSelectedSellerId(String(firstId));
        }
      } catch (err) {
        console.error("[SorterPage] Failed to fetch sellers:", err);
      }
    };
    fetchSellers();
  }, [isEditor, company_id]);

  // ── Fetch product settings (min images, file size) ────────
  useEffect(() => {
    if (!company_id) return;
    const fetchProductSettings = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(
          `${API_HOST}/api/general-settings/${company_id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setProductSettings({
          minImages: data.min_images ?? 0,
          fileSizeKb: data.file_size_kb ?? 5120,
        });
        setSettingsLoaded(true);
      } catch (err) {
        console.error("[SorterPage] Failed to fetch product settings:", err);
        setProductSettings({ minImages: 0, fileSizeKb: 5120 });
        setSettingsLoaded(true);
      }
    };
    fetchProductSettings();
  }, [company_id]);

  // ── Fetch active Shopify metafields (company-level) ────────
  useEffect(() => {
    if (!company_id) return;
    const fetchActiveMetafields = async () => {
      setLoadingMetafields(true);
      try {
        const res = await fetch(
          `${API_HOST}/api/v1/getActiveShopifyMetafields/${company_id}`,
        );
        const result = await res.json();
        if (result.success) {
          setActiveMetafields(result.data.active_metafields || []);
        }
      } catch (err) {
        console.error("[SorterPage] Failed to fetch active metafields:", err);
      } finally {
        setLoadingMetafields(false);
      }
    };
    fetchActiveMetafields();
  }, [company_id]);

  // ── Fetch saved results ───────────────────────────────────
  const fetchResults = useCallback(async () => {
    if (isEditor && !selectedSellerId) return;
    setPageState(PAGE_STATE.LOADING);
    setResults([]);
    try {
      const res = await axios.get(`${API_BASE}/smartscan/list-all`, {
        params: { company_id, user_id: activeUserId, role: userRole },
      });
      if (res.data.success) {
        setResults(res.data.data.map((item) => mapToResultRow(item)));
      }
    } catch (err) {
      console.error("[SorterPage] Failed to load saved results:", err);
    } finally {
      setPageState(PAGE_STATE.IDLE);
    }
  }, [company_id, activeUserId, userRole, isEditor, selectedSellerId]);

  useEffect(() => {
    fetchResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company_id, user_id, isEditor, selectedSellerId]);

  // Silent background refresh — same list, but doesn't clear the table or
  // show a loading spinner, so items scanned elsewhere (another device,
  // automation) show up here without the user having to hit refresh.
  const fetchResultsSilently = useCallback(async () => {
    if (isEditor && !selectedSellerId) return;
    try {
      const res = await axios.get(`${API_BASE}/smartscan/list-all`, {
        params: { company_id, user_id: activeUserId, role: userRole },
      });
      if (res.data.success) {
        setResults(res.data.data.map((item) => mapToResultRow(item)));
      }
    } catch (err) {
      console.error("[SorterPage] Silent refresh failed:", err);
    }
  }, [company_id, activeUserId, userRole, isEditor, selectedSellerId]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchResultsSilently();
    }, 15000);
    return () => clearInterval(interval);
  }, [fetchResultsSilently]);

  // ── Owner/admin/seller manager: fetch pending price requests ──
  const fetchPendingPriceRequests = useCallback(async () => {
    if (!canReviewPriceRequests || !company_id) return;
    setPriceRequestsLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/smartscan/price-requests`, {
        params: { company_id, status: "pending" },
      });
      if (res.data.success) {
        setPendingPriceRequests(res.data.data || []);
      }
    } catch (err) {
      console.error("[SorterPage] Failed to load price requests:", err);
    } finally {
      setPriceRequestsLoading(false);
    }
  }, [canReviewPriceRequests, company_id]);

  useEffect(() => {
    fetchPendingPriceRequests();
  }, [fetchPendingPriceRequests]);

  // ── Upload a single image to ERP ─────────────────────────
  const uploadImageToAzure = useCallback(async (id, file, preview) => {
    setImages((prev) =>
      prev.map((img) =>
        img.id === id
          ? {
              ...img,
              uploadStatus: UPLOAD_STATUS.UPLOADING,
            }
          : img,
      ),
    );

    try {
      const blob = await fetch(preview).then((r) => r.blob());

      const form = new FormData();

      form.append("images[]", blob, file?.name || "image.jpg");

      const res = await axios.post(`${API_BASE}/upload-image`, form, {
        timeout: 60000,
      });

      const url = res.data?.images?.[0]?.url || null;

      if (!url) {
        throw new Error("No Azure URL in response");
      }

      setImages((prev) =>
        prev.map((img) =>
          img.id === id
            ? {
                ...img,
                uploadStatus: UPLOAD_STATUS.SUCCESS,
                uploadedUrl: url,
              }
            : img,
        ),
      );
    } catch (err) {
      console.error(
        `[SorterPage] Azure upload failed for ${file?.name}:`,
        err.message,
      );

      setImages((prev) =>
        prev.map((img) =>
          img.id === id
            ? {
                ...img,
                uploadStatus: UPLOAD_STATUS.FAILED,
                error: "Corrupt or invalid image. Please remove or replace it.",
              }
            : img,
        ),
      );
    }
  }, []);
  // ── Add new images (with file type & size validation) ────
  const addImages = useCallback(
    (files) => {
      const validTypes = ["image/jpeg", "image/png", "image/webp"];
      const maxSize = productSettings.fileSizeKb * 1024;

      files.forEach((file) => {
        if (!validTypes.includes(file.type)) {
          alert(
            `${file.name} is not a valid image. Only JPG, PNG, and WebP are allowed.`,
          );
          return;
        }

        if (file.size > maxSize) {
          alert(`${file.name} exceeds ${productSettings.fileSizeKb} KB.`);
          return;
        }

        const id = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
        const reader = new FileReader();
        reader.onloadend = () => {
          const preview = reader.result;
          setImages((prev) => [
            ...prev,
            {
              id,
              file,
              preview,
              uploadStatus: UPLOAD_STATUS.PENDING,
              uploadedUrl: null,
              error: null,
            },
          ]);
          uploadImageToAzure(id, file, preview);
        };
        reader.readAsDataURL(file);
      });
    },
    [uploadImageToAzure, productSettings],
  );

  // ── Replace a failed image ────────────────────────────────
  const handleReplaceClick = (id) => {
    replacingIdRef.current = id;
    replaceInputRef.current?.click();
  };

  const handleReplaceFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file || !replacingIdRef.current) return;
    const id = replacingIdRef.current;
    replacingIdRef.current = null;
    e.target.value = "";
    const reader = new FileReader();
    reader.onloadend = () => {
      const preview = reader.result;
      setImages((prev) =>
        prev.map((img) =>
          img.id === id
            ? {
                ...img,
                file,
                preview,
                uploadStatus: UPLOAD_STATUS.PENDING,
                uploadedUrl: null,
                error: null,
              }
            : img,
        ),
      );
      uploadImageToAzure(id, file, preview);
    };
    reader.readAsDataURL(file);
  };

  const handleUploadAreaClick = (e) => {
    if (isBusy) return;
    if (e.target.closest(".remove-btn, .replace-btn, .preview-actions")) return;
    if (!requireSize()) return;
    fileInputRef.current?.click();
  };
  const handleFileInputChange = (e) => {
    if (!requireSize()) {
      e.target.value = "";
      return;
    }
    const files = Array.from(e.target.files || []);
    if (files.length > 0) addImages(files);
    e.target.value = "";
  };
  const handleDragOver = (e) => {
    if (!hasSize) return;
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDragDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!requireSize()) return;
    addImages(Array.from(e.dataTransfer.files));
  };
  const handleRemoveImage = (id) =>
    setImages((prev) => prev.filter((img) => img.id !== id));
  const handleClearAll = () => setImages([]);

  // ── Reorder images via drag-and-drop ──────────────────────
  const handleImageDragStart = (id) => (e) => {
    if (isBusy) {
      e.preventDefault();
      return;
    }
    e.stopPropagation();
    setDraggedImageId(id);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleImageDragOver = (id) => (e) => {
    if (isBusy || !draggedImageId || draggedImageId === id) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    setDragOverImageId(id);
  };

  const handleImageDragLeave = (e) => {
    setDragOverImageId(null);
  };

  const handleImageDrop = (dropId) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverImageId(null);
    if (!draggedImageId || draggedImageId === dropId) {
      setDraggedImageId(null);
      return;
    }
    setImages((prev) => {
      const dragIndex = prev.findIndex((img) => img.id === draggedImageId);
      const dropIndex = prev.findIndex((img) => img.id === dropId);
      if (dragIndex === -1 || dropIndex === -1) return prev;
      const newImages = [...prev];
      const [moved] = newImages.splice(dragIndex, 1);
      newImages.splice(dropIndex, 0, moved);
      return newImages;
    });
    setDraggedImageId(null);
  };

  const handleImageDragEnd = () => {
    setDraggedImageId(null);
    setDragOverImageId(null);
  };

  const handleClearFilters = () => {
    setActiveStatusTab("");
    setDateFrom("");
    setDateTo("");
  };

  // ── Usage limit check ─────────────────────────────────────
  const checkUsageLimit = async () => {
    if (company_id !== DEMO_COMPANY_ID) return true;
    try {
      const res = await axios.get(`${API_BASE}/smartscan/counts`, {
        params: { company_id },
      });
      if (!res.data?.success) return true;
      const { smartscan = 0, products = 0 } = res.data.data;
      if (Number(smartscan) >= DEMO_LIMIT || Number(products) >= DEMO_LIMIT) {
        alert(
          "Your SmartScan demo limit has been reached (100 items). Please contact ThriftOps support to upgrade your plan.",
        );
        return false;
      }
      return true;
    } catch {
      return true;
    }
  };

  // ── Vendor/seller: one-click price-increase request ────────
  // No modal, no manual typing. Fetches the current bounds for this
  // scan (backend computes: pending -> ±20% of payout; rejected ->
  // increase-only up to cost_floor*1.2) and immediately submits a
  // request for the max allowed payout.
  const handleQuickPriceRequest = async (result) => {
    const scanId = result?.raw?.id;
    if (!scanId) return;
    if (submittingPriceRequestIds.has(scanId)) return;

    setSubmittingPriceRequestIds((prev) => new Set(prev).add(scanId));

    try {
      const boundsRes = await axios.get(
        `${API_BASE}/smartscan/${scanId}/payout-bounds`,
      );
      if (!boundsRes.data.success) {
        throw new Error(boundsRes.data.error || "Could not fetch bounds");
      }
      const { max_allowed_payout, status } = boundsRes.data;

      const reqRes = await axios.post(
        `${API_BASE}/smartscan/${scanId}/price-request`,
        {
          new_price: max_allowed_payout,
          note:
            status === "rejected"
              ? "Auto-requested max allowed increase to relist"
              : "Auto-requested max allowed price increase",
          requested_by: user_id,
          role: userRole,
        },
      );

      if (reqRes.data.success) {
        alert(
          status === "rejected"
            ? `Increase request submitted (up to ${result.unit}${max_allowed_payout}). If approved, this item moves back to Pending.`
            : `Price increase request submitted (up to ${result.unit}${max_allowed_payout}) for review.`,
        );
      } else {
        alert(reqRes.data.error || "Failed to submit request.");
      }
    } catch (err) {
      console.error("[SorterPage] Quick price request failed:", err);
      alert(
        err.response?.data?.error ||
          "Failed to submit price request. Please try again.",
      );
    } finally {
      setSubmittingPriceRequestIds((prev) => {
        const next = new Set(prev);
        next.delete(scanId);
        return next;
      });
    }
  };

  // ── MAIN FLOW: Process images ─────────────────────────────
  const handleProcessImages = async () => {
    if (!canProcess) return;
    const canProceed = await checkUsageLimit();
    if (!canProceed) return;

    if (
      productSettings.minImages > 0 &&
      images.length < productSettings.minImages
    ) {
      alert(`Please upload at least ${productSettings.minImages} images.`);
      return;
    }

    const successImages = images.filter(
      (img) => img.uploadStatus === UPLOAD_STATUS.SUCCESS,
    );
    const uploadedUrls = successImages.map((img) => img.uploadedUrl);

    setPageState(PAGE_STATE.PROCESSING);
    setProcessingError(null);
    setAutoFillMsgIndex(0);

    let aiData;
    try {
      const aiForm = new FormData();
      successImages.forEach((img) => {
        if (img.file) aiForm.append("images", img.file);
      });
      aiForm.append("companyid", company_id || "");
      aiForm.append("size", trimmedSize);
      aiForm.append("userId", activeUserId || "");
      if (activeMetafields.length > 0) {
        aiForm.append("metafields", JSON.stringify(activeMetafields));
      }
      aiData = await axios
        .post(`${API_BASE}/multi-model/analyze`, aiForm, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((r) => r.data);
    } catch (err) {
      console.error("[SorterPage] AI analysis failed:", err);
      setPageState(PAGE_STATE.ERROR);
      setProcessingError("AI analysis failed. Please try again.");
      return;
    }

    setPageState(PAGE_STATE.SAVING);

    const analysis = aiData.analysis || {};
    const metadata = aiData.product_metadata || {};
    const price = aiData.price || {};
    const authenticity = aiData.authenticity || {};
    const condition = aiData.condition || {};
    const category = analysis.category || {};
    const authScore = authenticity.score ?? 0;
    const condScore = normalizeConditionScore(condition.score ?? 0);
    const itemStatus = isConditionAcceptable(
      condScore,
      minimumAcceptableCondition,
    )
      ? "pending"
      : "rejected";

    const mappedMetafields = buildMetafieldsFromAnalysis(
      activeMetafields,
      aiData,
    );
    const sizeMetafieldDef = activeMetafields.find(
      (mf) => mf.key?.toLowerCase() === "size",
    );
    if (sizeMetafieldDef && trimmedSize) {
      mappedMetafields[sizeMetafieldDef.key] = trimmedSize;
    }
    let savedScanId = null;
    let savedSize = trimmedSize;
    let savedFleek = null;
    let savedStatus = itemStatus;
    let savedRejectionReason = itemStatus === "rejected" ? "condition" : null;
    let savedPricing = null;

    try {
      const saveRes = await axios.post(`${API_BASE}/smartscan/save`, {
        company_id,
        user_id: activeUserId,
        analysisData: aiData,
        uploadedImageUrls: uploadedUrls,
        status: itemStatus,
        size: trimmedSize,
        role: userRole,
        metafields: mappedMetafields,
      });
      if (saveRes.data.success) {
        savedScanId = saveRes.data.smartscan_id ?? saveRes.data.id ?? null;
        savedSize = saveRes.data.size || trimmedSize;
        savedFleek = saveRes.data.fleek || null;
        savedPricing = saveRes.data.pricing || null;
        savedStatus = saveRes.data.status || itemStatus;
        savedRejectionReason =
          saveRes.data.rejection_reason ?? savedRejectionReason;
      } else {
        throw new Error(saveRes.data?.message || "Save failed");
      }
    } catch (saveErr) {
      console.error(
        "[SorterPage] Save failed:",
        saveErr.response?.data || saveErr.message,
      );
      setPageState(PAGE_STATE.ERROR);
      setProcessingError(
        "Analysis succeeded but saving failed. Please try again — no data was lost.",
      );
      return;
    }

    const rawItem = {
      id: savedScanId,
      user_id: activeUserId,
      title: metadata.title || analysis.seo_title || "Unknown Product",
      sku: metadata.sku || "",
      brand: analysis.brand || "",
      color: analysis.color || "",
      material: analysis.material || "",
      l1_gender: category.l1_gender || null,
      l2_type: category.l2_type || null,
      l3_category: category.l3_category || null,
      l4_subcategory: category.l4_subcategory || null,
      authenticity_score: authScore,
      authenticity_summary: authenticity.summary || "",
      condition_score: condScore,
      condition_summary: condition.summary || "",
      currency: price.currency || "USD",
      price_low: price.low ?? null,
      price_high: price.high ?? null,
      price_recommended: price.recommended ?? null,
      description: metadata.description || analysis.seo_description || "",
      seo_title: analysis.seo_title || "",
      seo_tags: analysis.seo_tags || metadata.seo_tags || "",
      sell_channel: getThriftopsSuggestion(authScore, condScore),
      sell_reason: aiData.thriftops_suggestion?.reason || "",
      images: uploadedUrls,
      size: savedSize,
      status: savedStatus,
      rejection_reason: savedRejectionReason,
      created_at: new Date().toISOString(),
      fleek_decision: savedFleek?.fleek_decision || null,
      fleek_list_price: savedFleek?.fleek_list_price ?? null,
      fleek_cost_floor: savedFleek?.fleek_cost_floor ?? null,
      fleek_reseller_cut: savedFleek?.fleek_reseller_cut ?? null,
      fleek_reason: savedFleek?.fleek_reason || null,
      pricing: savedPricing,
      metafields: mappedMetafields,
    };

    setResults((prev) => [
      mapToResultRow(rawItem, uploadedUrls[0] || null),
      ...prev,
    ]);
    setImages([]);
    setProductSize("");
    setSizeError(null);
    setPageState(PAGE_STATE.IDLE);

    setTimeout(() => {
      resultsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  // ── Editor: Status update ─────────────────────────────
  const handleStatusChange = async (index, newStatus) => {
    const result = filteredResults[index];
    if (!result?.raw) return;

    const smartscan_id = result.raw.id;
    if (!smartscan_id) return;

    try {
      await axios.patch(`${API_BASE}/smartscan/update-status`, {
        smartscan_id,
        status: newStatus,
      });

      setResults((prev) =>
        prev.map((r) =>
          r.raw?.id === smartscan_id
            ? { ...r, status: newStatus, raw: { ...r.raw, status: newStatus } }
            : r,
        ),
      );
    } catch (err) {
      console.error("[SorterPage] Status update failed:", err);
      alert("Status update failed. Please try again.");
    }
  };

  // ── Seller/vendor: size-only edit ─────────────────────────
  const startEditSize = (result) => {
    setEditingSizeId(result.raw?.id);
    setSizeDraft(result.size || "");
  };

  const cancelEditSize = () => {
    setEditingSizeId(null);
    setSizeDraft("");
  };

  const saveEditSize = async (result) => {
    const smartscan_id = result.raw?.id;
    if (!smartscan_id) return;
    const trimmed = sizeDraft.trim();
    if (!trimmed) return;

    try {
      await axios.patch(`${API_BASE}/smartscan/update-size`, {
        smartscan_id,
        size: trimmed,
      });
      setResults((prev) =>
        prev.map((r) =>
          r.raw?.id === smartscan_id
            ? { ...r, size: trimmed, raw: { ...r.raw, size: trimmed } }
            : r,
        ),
      );
      setEditingSizeId(null);
      setSizeDraft("");
    } catch (err) {
      console.error("[SorterPage] Size update failed:", err);
      alert("Size update failed. Please try again.");
    }
  };

  // Moves an item into the Need Wash / Need Repair queue. Does NOT clear the
  // needs_wash/needs_repair flag — that still happens only when the item is
  // handed over at the end of the Care Queue workflow. Until this is
  // clicked, the item stays hidden from the Need Wash/Repair screens even
  // though the AI already flagged it.
  const moveToCare = async (result, type) => {
    // type: "repair" or "wash"
    const smartscan_id = result.raw?.id;
    if (!smartscan_id) return;

    try {
      await axios.patch(`${API_BASE}/smartscan/move-to-care`, {
        smartscan_id,
        type,
      });
      setResults((prev) =>
        prev.map((r) =>
          r.raw?.id === smartscan_id
            ? { ...r, careMoved: { ...(r.careMoved || {}), [type]: true } }
            : r,
        ),
      );
    } catch (err) {
      console.error("[SorterPage] Move to care failed:", err);
      alert("Could not update. Please try again.");
    }
  };

  // const saveEditCare = async (result) => {
  //   const smartscan_id = result.raw?.id;
  //   if (!smartscan_id) return;
  //   try {
  //     await axios.patch(`${API_BASE}/smartscan/update-care`, {
  //       smartscan_id,
  //       needs_repair: careDraft.needsRepair,
  //       needs_wash: careDraft.needsWash,
  //       repair_types: careDraft.repairTypes,
  //       care_reason: careDraft.careReason,
  //     });
  //     setResults((prev) =>
  //       prev.map((r) =>
  //         r.raw?.id === smartscan_id
  //           ? {
  //               ...r,
  //               needsRepair: careDraft.needsRepair,
  //               needsWash: careDraft.needsWash,
  //               repairTypes: careDraft.repairTypes,
  //               careReason: careDraft.careReason,
  //             }
  //           : r,
  //       ),
  //     );
  //     setEditingCareId(null);
  //   } catch (err) {
  //     console.error("[SorterPage] Care update failed:", err);
  //     alert("Care update failed. Please try again.");
  //   }
  // };
  // ── Sell Online ───────────────────────────────────────────
  const handleSellOnline = (index) => {
    const result = filteredResults[index];

    if (!result?.raw) return;
    const item = result.raw;

    const categoryString = [
      item.l1_gender,
      item.l2_type,
      item.l3_category,
      item.l4_subcategory,
    ]
      .filter(Boolean)
      .join(" > ");
    const priceRange =
      item.price_low != null && item.price_high != null
        ? `${item.price_low} - ${item.price_high}`
        : "";
    const conditionScore = normalizeConditionScore(item.condition_score);
    const conditionLabel =
      conditionScore >= 10
        ? "NEW"
        : conditionScore >= 9
          ? "LIKE-NEW"
          : conditionScore >= 8
            ? "EXCELLENT"
            : conditionScore >= 7
              ? "GOOD"
              : conditionScore >= 6
                ? "FAIR"
                : "POOR";
    const tags = item.seo_tags
      ? item.seo_tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean)
      : [];
    const uploadedImages = Array.isArray(item.images) ? item.images : [];
    const itemSize = item.size || result.size || "";

    setAddProductState({
      currentStep: 2,
      formData: {
        uploadedImages,
        categoryAspects: [],
        allCategoryAspects: [],
        categoryId: "",
        blurStatuses: uploadedImages.map(() => false),
        imageProcessing: false,
        backgroundRemoval: false,
        blurDetection: false,
        upscaleImage: false,
        title: item.title || "",
        description: item.description || "",
        sku: item.sku || "",
        price: result.price,
        pricerange: priceRange,
        quantity: "1",
        brand: item.brand || "",
        color: item.color || "",
        category: categoryString,
        condition: conditionLabel,
        condition_score: item.condition_score ?? "",
        condition_summary: item.condition_summary || "",
        authenticity_score: item.authenticity_score || "",
        authenticity_summary: item.authenticity_summary || "",
        material: item.material || "",
        tags,
        metafields: item.metafields || {},
        variants: [{ id: 1, size: itemSize, color: "", sku: "" }],
        selectedMarketplaces: [],
        poshmarkCategory: "Women",
        poshmarkSubCategory: "",
        poshmarkSizeType: "standard",
        poshmarkAvailability: "available",
        vintedCategory: "",
        vintedCategoryId: "",
        vintedCategoryPath: [],
        vintedPackageSize: "medium",
        vintedSize: itemSize,
        size: itemSize,
        seller_id: isEditor ? selectedSellerId : item.user_id,
        _sorterAiResponse: {
          analysis: {
            seo_title: item.title,
            seo_description: item.description,
            brand: item.brand,
            color: item.color,
            material: item.material,
            category: {
              l1_gender: item.l1_gender,
              l2_type: item.l2_type,
              l3_category: item.l3_category,
              l4_subcategory: item.l4_subcategory,
            },
            condition_summary: item.condition_summary,
            seo_tags: item.seo_tags,
            metafields: item.metafields || {},
          },
          authenticity: {
            score: item.authenticity_score,
            summary: item.authenticity_summary,
          },
          condition: {
            score: item.condition_score,
            summary: item.condition_summary,
          },
          price: {
            recommended: item.price_recommended,
            low: item.price_low,
            high: item.price_high,
          },
        },
      },
    });
    navigate("/products/add");
  };

  // ── Image edit / reorder / swap modal ─────────────────────
  const startEditImages = (result) => {
    if (!result?.raw?.id) return;
    const imgs = Array.isArray(result.raw.images) ? result.raw.images : [];
    if (imgs.length === 0) {
      alert("No images available for this product.");
      return;
    }
    setEditingResultId(result.raw.id);
    setEditingImages(
      imgs.map((url, i) => ({ id: `img_${i}_${Date.now()}`, url, index: i })),
    );
    setImageEditModalOpen(true);
  };

  // ── Mobile touch drag handlers (Image Edit Modal) ──
  const handleModalTouchStart = (imgId) => (e) => {
    e.preventDefault();
    setDraggedImageId(imgId);
  };

  const handleModalTouchMove = (e) => {
    e.preventDefault();
    const touch = e.touches[0];
    const target = document.elementFromPoint(touch.clientX, touch.clientY);
    let el = target;
    while (el && !el.classList.contains("image-edit-card")) {
      el = el.parentElement;
    }
    if (el) {
      const overId = el.getAttribute("data-img-id");
      if (overId && overId !== draggedImageId) {
        setDragOverImageId(overId);
      }
    } else {
      setDragOverImageId(null);
    }
  };

  const handleModalTouchEnd = (e) => {
    e.preventDefault();
    if (
      draggedImageId &&
      dragOverImageId &&
      draggedImageId !== dragOverImageId
    ) {
      setEditingImages((prev) => {
        const dragIndex = prev.findIndex((img) => img.id === draggedImageId);
        const dropIndex = prev.findIndex((img) => img.id === dragOverImageId);
        if (dragIndex === -1 || dropIndex === -1) return prev;
        const newImages = [...prev];
        const [moved] = newImages.splice(dragIndex, 1);
        newImages.splice(dropIndex, 0, moved);
        return newImages.map((img, i) => ({ ...img, index: i }));
      });
    }
    setDraggedImageId(null);
    setDragOverImageId(null);
  };

  const closeImageEditModal = () => {
    setImageEditModalOpen(false);
    setEditingResultId(null);
    setEditingImages([]);
    setImageEditSaving(false);
  };

  const handleModalImageDragStart = (id) => (e) => {
    e.stopPropagation();
    setDraggedImageId(id);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleModalImageDragOver = (id) => (e) => {
    if (!draggedImageId || draggedImageId === id) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    setDragOverImageId(id);
  };

  const handleModalImageDragLeave = () => setDragOverImageId(null);

  const handleModalImageDrop = (dropId) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverImageId(null);
    if (!draggedImageId || draggedImageId === dropId) {
      setDraggedImageId(null);
      return;
    }
    setEditingImages((prev) => {
      const dragIndex = prev.findIndex((img) => img.id === draggedImageId);
      const dropIndex = prev.findIndex((img) => img.id === dropId);
      if (dragIndex === -1 || dropIndex === -1) return prev;
      const newImages = [...prev];
      const [moved] = newImages.splice(dragIndex, 1);
      newImages.splice(dropIndex, 0, moved);
      return newImages.map((img, i) => ({ ...img, index: i }));
    });
    setDraggedImageId(null);
  };

  const handleModalImageDragEnd = () => {
    setDraggedImageId(null);
    setDragOverImageId(null);
  };

  const handleSaveImageEdits = async () => {
    if (!editingResultId) return;
    const reorderedUrls = editingImages.map((img) => img.url);
    setImageEditSaving(true);
    try {
      await axios.patch(`${API_BASE}/smartscan/update-images`, {
        smartscan_id: editingResultId,
        images: reorderedUrls,
      });
      setResults((prev) =>
        prev.map((r) =>
          r.raw?.id === editingResultId
            ? {
                ...r,
                image: reorderedUrls[0] || null,
                raw: { ...r.raw, images: reorderedUrls },
              }
            : r,
        ),
      );
      closeImageEditModal();
    } catch (err) {
      console.error("[SorterPage] Image update failed:", err);
      alert("Image update failed. Please try again.");
    } finally {
      setImageEditSaving(false);
    }
  };

  // ── Owner/admin/seller manager: review a pending price request ──
  const openReviewModal = async (request) => {
    setReviewTarget(request);
    setReviewError(null);
    setReviewBreakdown(null);
    setReviewModalOpen(true);

    setReviewBreakdownLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE}/smartscan/price-requests/${request.id}/breakdown`,
        { params: { role: userRole } },
      );
      if (res.data.success) {
        setReviewBreakdown(res.data);
      } else {
        setReviewError(res.data.error || "Failed to load breakdown.");
      }
    } catch (err) {
      console.error("[SorterPage] Failed to load breakdown:", err);
      setReviewError(err.response?.data?.error || "Failed to load breakdown.");
    } finally {
      setReviewBreakdownLoading(false);
    }
  };

  const closeReviewModal = () => {
    setReviewModalOpen(false);
    setReviewTarget(null);
    setReviewBreakdown(null);
    setReviewError(null);
    setReviewSubmitting(false);
  };

  const submitReview = async (action) => {
    if (!reviewTarget?.id) return;
    setReviewSubmitting(true);
    setReviewError(null);
    try {
      const res = await axios.patch(
        `${API_BASE}/smartscan/price-requests/${reviewTarget.id}`,
        {
          action,
          reviewed_by: user_id,
          role: userRole,
        },
      );
      if (res.data.success) {
        closeReviewModal();
        setPendingPriceRequests((prev) =>
          prev.filter((r) => r.id !== reviewTarget.id),
        );
        if (action === "approve") {
          fetchResults();
        }
      } else {
        setReviewError(res.data.error || "Failed to submit review.");
      }
    } catch (err) {
      console.error("[SorterPage] Review submit failed:", err);
      setReviewError(
        err.response?.data?.error ||
          "Failed to submit review. Please try again.",
      );
    } finally {
      setReviewSubmitting(false);
    }
  };

  // Quick-reject straight from the list — no breakdown needed since
  // rejecting has zero financial impact. Approve still always opens
  // the breakdown modal first.
  const quickReject = async (request) => {
    if (
      !window.confirm(
        `Reject this price change request for "${request.title}"?`,
      )
    )
      return;
    try {
      const res = await axios.patch(
        `${API_BASE}/smartscan/price-requests/${request.id}`,
        { action: "reject", reviewed_by: user_id, role: userRole },
      );
      if (res.data.success) {
        setPendingPriceRequests((prev) =>
          prev.filter((r) => r.id !== request.id),
        );
      }
    } catch (err) {
      console.error("[SorterPage] Quick reject failed:", err);
      alert(
        err.response?.data?.error ||
          "Failed to reject request. Please try again.",
      );
    }
  };

  // Groups the flat pending-requests list by seller/vendor for display.
  const priceRequestsBySeller = useMemo(() => {
    const groups = new Map();
    pendingPriceRequests.forEach((req) => {
      const key = req.requested_by_name || `User #${req.requested_by}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(req);
    });
    return Array.from(groups.entries());
  }, [pendingPriceRequests]);

  // ── Modal ─────────────────────────────────────────────────
  const openReasonModal = (type, score, reasons, description) => {
    setModalData({ type, score, reasons: reasons || [], description });
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setModalData(null);
  };
  const handleDismissError = () => {
    setProcessingError(null);
    setPageState(PAGE_STATE.IDLE);
  };

  // ── Render image upload status ────────────────────────────
  const renderImageStatus = (img) => {
    switch (img.uploadStatus) {
      case UPLOAD_STATUS.UPLOADING:
        return (
          <div className="upload-status uploading">
            <div className="spinner" />
            <span>Uploading...</span>
          </div>
        );
      case UPLOAD_STATUS.SUCCESS:
        return (
          <div className="upload-status success">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>Uploaded</span>
          </div>
        );
      case UPLOAD_STATUS.FAILED:
        return (
          <div className="upload-status failed">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
            <span>{img.error}</span>
          </div>
        );
      default:
        return null;
    }
  };

  const hasActiveFilters = activeStatusTab || dateFrom || dateTo;

  // ─────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────
  return (
    <>
      {(pageState === PAGE_STATE.PROCESSING ||
        pageState === PAGE_STATE.SAVING) && (
        <AutoFillLoader
          message={
            pageState === PAGE_STATE.SAVING
              ? "Saving your results..."
              : AUTO_FILL_MESSAGES[autoFillMsgIndex]
          }
        />
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: "none" }}
        onChange={handleFileInputChange}
      />
      <input
        ref={replaceInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleReplaceFileChange}
      />

      <div className="sorter-page">
        <div className="sorter-container">
          {/* ── Editor: Seller selector ────────────────────── */}
          {isEditor && (
            <div className="editor-seller-filter">
              <label htmlFor="seller-select">
                <span className="seller-filter-icon">👤</span>
                Viewing SmartScan for:
              </label>
              {sellerList.length === 0 ? (
                <span className="seller-filter-empty">
                  No sellers found in this company
                </span>
              ) : (
                <select
                  id="seller-select"
                  className="seller-select"
                  value={selectedSellerId || ""}
                  onChange={(e) => {
                    setSelectedSellerId(e.target.value);
                    setResults([]);
                  }}
                >
                  {sellerList.map((seller) => {
                    const id = seller.user_id || seller.id || seller.userId;
                    const name =
                      seller.storeName ||
                      seller.store_name ||
                      seller.name ||
                      seller.username ||
                      seller.email ||
                      `Seller #${id}`;
                    return (
                      <option key={id} value={String(id)}>
                        {name}
                      </option>
                    );
                  })}
                </select>
              )}
              {isEditor && selectedSellerId && (
                <span className="seller-scan-note">
                  📌 Scans will be saved under{" "}
                  <strong>{selectedSellerName}</strong>
                </span>
              )}
            </div>
          )}

          {/* ── Error banner ───────────────────────────────── */}
          {pageState === PAGE_STATE.ERROR && processingError && (
            <div className="error-banner">
              <span>{processingError}</span>
              <button onClick={handleDismissError}>Dismiss</button>
            </div>
          )}

          {/* ── Owner/admin/seller manager: pending price requests ── */}
          {canReviewPriceRequests && (
            <div className="price-requests-panel">
              <div className="price-requests-panel-header">
                <h2>Price Change Requests</h2>
                {pendingPriceRequests.length > 0 && (
                  <span className="price-requests-count-badge">
                    {pendingPriceRequests.length} pending
                  </span>
                )}
              </div>

              {priceRequestsLoading ? (
                <p className="price-requests-empty">Loading requests...</p>
              ) : priceRequestsBySeller.length === 0 ? (
                <p className="price-requests-empty">
                  No pending price change requests.
                </p>
              ) : (
                priceRequestsBySeller.map(([sellerName, requests]) => (
                  <div key={sellerName} className="price-requests-seller-group">
                    <h3 className="price-requests-seller-name">
                      👤 {sellerName}
                      <span className="price-requests-seller-count">
                        {requests.length}
                      </span>
                    </h3>
                    <div className="price-requests-list">
                      {requests.map((req) => (
                        <div key={req.id} className="price-request-card">
                          <div className="price-request-card-info">
                            <p className="price-request-card-title">
                              {req.title}
                            </p>
                            <p className="price-request-card-sku">{req.sku}</p>
                            <div className="price-request-card-prices">
                              <span className="price-request-card-old">
                                {req.old_price}
                              </span>
                              <span className="price-request-card-arrow">
                                →
                              </span>
                              <span
                                className={`price-request-card-new price-request-card-new--${req.change_type}`}
                              >
                                {req.new_price}
                              </span>
                              <span
                                className={`price-request-card-pct price-request-card-pct--${req.change_type}`}
                              >
                                {req.change_type === "increase" ? "+" : ""}
                                {req.change_percent}%
                              </span>
                            </div>
                            {req.note && (
                              <p className="price-request-card-note">
                                "{req.note}"
                              </p>
                            )}
                          </div>
                          <div className="price-request-card-actions">
                            <button
                              className="btn-primary btn-sm"
                              onClick={() => openReviewModal(req)}
                            >
                              Review
                            </button>
                            <button
                              className="btn-secondary btn-sm"
                              onClick={() => quickReject(req)}
                            >
                              Reject
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* ── Size input (required before upload) ──────── */}
          <div className="sorter-size-field">
            <label htmlFor="product-size">
              <span className="size-field-icon"></span>
              Product Size
              <span className="required-mark">*</span>
            </label>
            <input
              id="product-size"
              type="text"
              className={`size-input ${sizeError ? "size-input--error" : ""}`}
              placeholder="e.g. M, 10, 32x30"
              value={productSize}
              onChange={(e) => {
                setProductSize(e.target.value);
                if (e.target.value.trim()) setSizeError(null);
              }}
              disabled={isBusy}
              autoComplete="off"
            />
            {sizeError ? (
              <p className="size-field-error">{sizeError}</p>
            ) : (
              <p className="size-field-hint">
                Enter the size first, then upload your images.
              </p>
            )}
          </div>

          {/* ── Upload section ─────────────────────────────── */}
          <div className="image-preview-section sorter-upload-section">
            <div
              className={`sorter-upload-area ${!hasSize || isBusy ? "sorter-upload-area--disabled" : ""}`}
              onClick={handleUploadAreaClick}
              onDragOver={handleDragOver}
              onDrop={handleDragDrop}
            >
              {images.length > 0 ? (
                <div className="images-grid sorter-image-preview-grid">
                  {images.map((img) => (
                    <div
                      key={img.id}
                      draggable={
                        !isBusy && img.uploadStatus !== UPLOAD_STATUS.UPLOADING
                      }
                      onDragStart={handleImageDragStart(img.id)}
                      onDragOver={handleImageDragOver(img.id)}
                      onDragLeave={handleImageDragLeave}
                      onDrop={handleImageDrop(img.id)}
                      onDragEnd={handleImageDragEnd}
                      className={`preview-card preview-card--${img.uploadStatus} ${draggedImageId === img.id ? "preview-card--dragging" : ""} ${dragOverImageId === img.id && draggedImageId !== img.id ? "preview-card--drag-over" : ""}`}
                      style={{ cursor: isBusy ? "default" : "move" }}
                    >
                      <button
                        type="button"
                        className="remove-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveImage(img.id);
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                      <img
                        src={img.preview}
                        alt={img.file?.name || "Product upload"}
                        className="preview-image"
                        loading="lazy"
                      />
                      {renderImageStatus(img)}
                      {img.uploadStatus === UPLOAD_STATUS.FAILED && (
                        <button
                          type="button"
                          className="replace-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleReplaceClick(img.id);
                          }}
                        >
                          Replace Image
                        </button>
                      )}
                      <div className="preview-info">
                        <p className="file-name">{img.file?.name || "Image"}</p>
                        <p className="file-size">
                          {img.file?.size
                            ? `${(img.file.size / 1024).toFixed(1)} KB`
                            : ""}
                        </p>
                      </div>
                    </div>
                  ))}
                  {!isBusy && (
                    <div className="sorter-add-more-card">
                      <div className="upload-icon">+</div>
                      <p>Add more images</p>
                      <span className="add-more-hint">Click or drop here</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="upload-placeholder">
                  <div className="upload-icon">📷</div>
                  <h3>
                    {hasSize
                      ? "Drop Images OR Click to Upload"
                      : "Enter size above to upload images"}
                  </h3>
                  <p>
                    JPG, PNG, WebP • Max {productSettings.fileSizeKb / 1024}MB
                  </p>
                </div>
              )}
            </div>

            {images.length > 0 && (
              <div className="preview-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleClearAll();
                  }}
                  disabled={isBusy}
                  style={{ touchAction: "manipulation" }}
                >
                  Clear All ({images.length})
                </button>
                <div className="process-btn-wrapper">
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleProcessImages();
                    }}
                    disabled={!canProcess}
                    style={{ touchAction: "manipulation" }}
                    title={
                      !hasSize
                        ? "Enter a product size first"
                        : anyFailed
                          ? "Remove or replace failed images first"
                          : anyUploading
                            ? "Please wait for uploads to complete"
                            : anyPending
                              ? "Please wait..."
                              : ""
                    }
                  >
                    {anyUploading || anyPending
                      ? "Uploading..."
                      : `Process ${images.length} Image${images.length !== 1 ? "s" : ""}`}
                  </button>
                  {!hasSize && (
                    <p className="process-btn-hint error">
                      Enter a product size to continue.
                    </p>
                  )}
                  {anyFailed && (
                    <p className="process-btn-hint error">
                      Some images failed to upload. Remove or replace them to
                      continue.
                    </p>
                  )}
                  {anyUploading && (
                    <p className="process-btn-hint">
                      Waiting for uploads to finish...
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── Loading state ──────────────────────────────── */}
          {pageState === PAGE_STATE.LOADING && (
            <div className="results-loading">
              {isEditor && selectedSellerId
                ? `Loading scans for ${selectedSellerName}...`
                : "Loading your saved scans..."}
            </div>
          )}

          {/* ── Status tabs + Date filters ─────────────────── */}
          {results.length > 0 && pageState !== PAGE_STATE.LOADING && (
            <div className="scan-filters-bar">
              <div className="scan-status-tabs" role="tablist">
                {SCAN_STATUS_TABS.map((tab) => (
                  <button
                    key={tab.key || "all"}
                    type="button"
                    role="tab"
                    aria-selected={activeStatusTab === tab.key}
                    className={`scan-tab-btn ${activeStatusTab === tab.key ? "active" : ""}`}
                    onClick={() => setActiveStatusTab(tab.key)}
                  >
                    {tab.label}
                    <span className="scan-tab-count">
                      {statusCounts[tab.key] ?? 0}
                    </span>
                  </button>
                ))}
              </div>

              <div className="scan-date-filter">
                <label>From</label>
                <input
                  type="date"
                  className="scan-date-input"
                  value={dateFrom}
                  max={dateTo || undefined}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
                <label>To</label>
                <input
                  type="date"
                  className="scan-date-input"
                  value={dateTo}
                  min={dateFrom || undefined}
                  onChange={(e) => setDateTo(e.target.value)}
                />
                {hasActiveFilters && (
                  <button
                    type="button"
                    className="scan-clear-btn"
                    onClick={handleClearFilters}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ── Results table ──────────────────────────────── */}
          {results.length > 0 && pageState !== PAGE_STATE.LOADING && (
            <div className="results-section" ref={resultsRef}>
              <div className="results-header">
                <h2 className="results-title">
                  {isEditor
                    ? `SmartScan Results — ${selectedSellerName}`
                    : "Processing Results"}
                </h2>
                <span className="results-count">
                  {filteredResults.length !== results.length
                    ? `${filteredResults.length} of ${results.length} items`
                    : `${results.length} item${results.length !== 1 ? "s" : ""}`}
                </span>
              </div>

              {filteredResults.length === 0 ? (
                <div className="results-empty">
                  <p>No results match your current filters.</p>
                  <button
                    type="button"
                    className="scan-clear-btn"
                    style={{ marginTop: "0.5rem" }}
                    onClick={handleClearFilters}
                  >
                    Clear Filters
                  </button>
                </div>
              ) : (
                <div className="result-table-wrapper">
                  <table className="result-table">
                    <thead>
                      <tr>
                        <th>Image</th>
                        <th>Product Description</th>
                        <th>Size</th>
                        <th>Potential Price</th>
                        {!isRestricted && <th>TO Price</th>}
                        <th>Auth Score</th>
                        <th>Condition</th>
                        <th>Suggestion</th>
                        <th>Status</th>
                        <th>Care</th>
                        <th>SKU</th>
                        <th>Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredResults.map((result, index) => (
                        <tr key={index} className="result-row">
                          <td className="image-cell" data-label="Image">
                            {result.image && (
                              <img
                                src={result.image}
                                alt={result.description}
                                className="result-thumbnail"
                                loading="lazy"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = PLACEHOLDER_IMG;
                                }}
                              />
                            )}
                          </td>
                          <td
                            className="description-cell"
                            data-label="Product Description"
                          >
                            <div className="description-text">
                              {result.description}
                            </div>
                          </td>
                          <td className="size-cell" data-label="Size">
                            {isRestricted ? (
                              editingSizeId === result.raw?.id ? (
                                <span className="size-edit-row">
                                  <input
                                    type="text"
                                    className="size-edit-input"
                                    value={sizeDraft}
                                    onChange={(e) =>
                                      setSizeDraft(e.target.value)
                                    }
                                    autoFocus
                                  />
                                  <button
                                    type="button"
                                    className="size-edit-save"
                                    onClick={() => saveEditSize(result)}
                                    title="Save size"
                                  >
                                    ✓
                                  </button>
                                  <button
                                    type="button"
                                    className="size-edit-cancel"
                                    onClick={cancelEditSize}
                                    title="Cancel"
                                  >
                                    ✕
                                  </button>
                                </span>
                              ) : (
                                <span
                                  className="size-text size-text--editable"
                                  onClick={() => startEditSize(result)}
                                  title="Click to edit size"
                                >
                                  {result.size || "—"} ✏️
                                </span>
                              )
                            ) : (
                              <span className="size-text">
                                {result.size || "—"}
                              </span>
                            )}
                          </td>
                          <td
                            className="price-cell"
                            data-label="Potential Price"
                          >
                            {result.priceRecommended != null &&
                            result.vendorPayout != null ? (
                              <div className="price-dual">
                                <span className="price-value">
                                  {result.unit}
                                  {result.priceRecommended}
                                </span>
                                <span
                                  className="price-subvalue"
                                  title="Vendor payout"
                                >
                                  Payout: {result.unit}
                                  {result.vendorPayout}
                                </span>
                              </div>
                            ) : (
                              <span className="price-value">
                                {result.price != null
                                  ? `${result.unit}${result.price}`
                                  : "—"}
                              </span>
                            )}
                          </td>
                          {!isRestricted && (
                            <td className="fleek-cell" data-label="TO Price">
                              {result.fleekDecision ? (
                                <span
                                  className={`fleek-badge fleek-badge--${result.fleekDecision.toLowerCase()}`}
                                  title={
                                    result.fleekDecision === "LIST"
                                      ? `List @ ${result.unit}${result.fleekListPrice} (cost floor ${result.unit}${result.fleekCostFloor})`
                                      : result.fleekReason || "Below cost floor"
                                  }
                                >
                                  {result.fleekDecision === "LIST"
                                    ? `${result.unit}${result.fleekListPrice}`
                                    : "—"}
                                </span>
                              ) : (
                                <span className="fleek-badge fleek-badge--none">
                                  —
                                </span>
                              )}
                            </td>
                          )}
                          <td className="score-cell" data-label="Auth Score">
                            <div className="score-with-icon">
                              <div className="score-badge score-auth">
                                {result.authScore}%
                              </div>
                              <button
                                className="eye-icon-btn"
                                onClick={() =>
                                  openReasonModal(
                                    "Authentication",
                                    result.authScore,
                                    result.authReasons,
                                    result.description,
                                  )
                                }
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                  <circle cx="12" cy="12" r="3" />
                                </svg>
                              </button>
                            </div>
                          </td>
                          <td className="score-cell" data-label="Condition">
                            <div className="score-with-icon">
                              <div className="score-badge score-condition">
                                {result.conditionScore}/10
                              </div>
                              <button
                                className="eye-icon-btn"
                                onClick={() =>
                                  openReasonModal(
                                    "Condition",
                                    result.conditionScore,
                                    result.conditionReasons,
                                    result.description,
                                  )
                                }
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                  <circle cx="12" cy="12" r="3" />
                                </svg>
                              </button>
                            </div>
                          </td>
                          <td
                            className="suggestion-cell"
                            data-label="Suggestion"
                          >
                            <span className="suggestion-text">
                              {result.suggestion}
                            </span>
                          </td>
                          <td className="status-cell" data-label="Status">
                            <StatusBadge status={result.status} />
                            {result.status === "rejected" &&
                              result.rejectionReason && (
                                <span
                                  className="rejection-reason-tag"
                                  title={`Rejected due to: ${result.rejectionReason}`}
                                >
                                  {result.rejectionReason
                                    .split(",")
                                    .map((r) => r.trim())
                                    .map(
                                      (r) =>
                                        r.charAt(0).toUpperCase() + r.slice(1),
                                    )
                                    .join(" + ")}
                                </span>
                              )}
                            {isEditor && (
                              <select
                                style={{
                                  marginTop: "6px",
                                  display: "block",
                                  fontSize: "0.72rem",
                                  padding: "2px 6px",
                                  borderRadius: "6px",
                                  border: "1px solid #d1d5db",
                                  cursor: "pointer",
                                }}
                                value={result.status}
                                onChange={(e) =>
                                  handleStatusChange(index, e.target.value)
                                }
                              >
                                <option value="pending">Pending</option>
                                <option value="rejected">Rejected</option>
                                <option value="uploaded">Uploaded</option>
                              </select>
                            )}
                          </td>
                          <td className="care-cell" data-label="Care">
                            {result.needsRepair || result.needsWash ? (
                              <div className="care-explanation">
                                <strong className="care-label">
                                  {result.needsWash && "🧺 Wash"}
                                  {result.needsWash &&
                                    result.needsRepair &&
                                    " + "}
                                  {result.needsRepair &&
                                    `🧵 Repair${
                                      result.repairTypes?.length
                                        ? ` (${result.repairTypes.join(", ")})`
                                        : ""
                                    }`}
                                </strong>
                                <p className="care-reason-text">
                                  {result.careReason}
                                </p>
                              </div>
                            ) : (
                              <span className="care-none">No care needed</span>
                            )}
                          </td>
                          <td className="sku" data-label="sku">
                            <span className="sku-text">
                              {!result.sku ||
                              /^SKU-\d+$/.test(result.sku.trim())
                                ? "-"
                                : result.sku}
                            </span>
                          </td>
                          <td className="date-cell" data-label="Date">
                            <span className="date-text">
                              {formatDate(result.createdAt)}
                            </span>
                          </td>
                          <td className="actions-cell" data-label="Actions">
                            {result.needsRepair || result.needsWash ? (
                              <div className="action-buttons care-blocking-actions">
                                {result.needsWash &&
                                  (result.careMoved?.wash ? (
                                    <span
                                      className="btn-sm"
                                      style={{ color: "#16a34a" }}
                                    >
                                      ✓ Moved to Wash queue
                                    </span>
                                  ) : (
                                    <button
                                      className="btn-primary btn-sm"
                                      onClick={() => moveToCare(result, "wash")}
                                    >
                                      Move to Wash
                                    </button>
                                  ))}
                                {result.needsRepair &&
                                  (result.careMoved?.repair ? (
                                    <span
                                      className="btn-sm"
                                      style={{ color: "#16a34a" }}
                                    >
                                      ✓ Moved to Repair queue
                                    </span>
                                  ) : (
                                    <button
                                      className="btn-primary btn-sm"
                                      onClick={() =>
                                        moveToCare(result, "repair")
                                      }
                                    >
                                      Move to Repair
                                      {result.repairTypes?.length
                                        ? ` (${result.repairTypes.join(", ")})`
                                        : ""}
                                    </button>
                                  ))}
                              </div>
                            ) : (
                              <div className="action-buttons">
                                {!isRestricted && (
                                  <>
                                    <button
                                      className="btn-primary btn-sm"
                                      onClick={() => handleSellOnline(index)}
                                      disabled={
                                        !isEditor &&
                                        !isConditionAcceptable(
                                          result.conditionScore,
                                          minimumAcceptableCondition,
                                        )
                                      }
                                      title={
                                        !isConditionAcceptable(
                                          result.conditionScore,
                                          minimumAcceptableCondition,
                                        )
                                          ? isEditor
                                            ? "Condition override active"
                                            : "Product condition is not acceptable"
                                          : "Sell online"
                                      }
                                    >
                                      Sell Online
                                    </button>

                                    {isEditor &&
                                      !isConditionAcceptable(
                                        result.conditionScore,
                                        minimumAcceptableCondition,
                                      ) && (
                                        <button
                                          className="btn-sm"
                                          onClick={() =>
                                            handleSellOnline(index)
                                          }
                                          style={{
                                            fontSize: "0.7rem",
                                            padding: "3px 8px",
                                            borderRadius: "6px",
                                            border: "1px solid #f59e0b",
                                            background: "#fffbeb",
                                            color: "#92400e",
                                            cursor: "pointer",
                                            marginTop: "4px",
                                          }}
                                          title="Override system rejection and sell anyway"
                                        >
                                          ⚠️ Override & Sell
                                        </button>
                                      )}

                                    <button
                                      className="btn-secondary btn-sm"
                                      disabled
                                      title="Coming soon"
                                    >
                                      Push to Retail
                                    </button>
                                    <button
                                      className="btn-secondary btn-sm"
                                      disabled
                                      title="Coming soon"
                                    >
                                      Wholesale
                                    </button>
                                  </>
                                )}

                                {isRestricted &&
                                  (result.status === "pending" ||
                                    result.status === "rejected") && (
                                    <button
                                      className="btn-secondary btn-sm"
                                      onClick={() =>
                                        handleQuickPriceRequest(result)
                                      }
                                      disabled={submittingPriceRequestIds.has(
                                        result.raw?.id,
                                      )}
                                      title={
                                        result.status === "rejected"
                                          ? "One click: request the max allowed increase to get this item relisted"
                                          : "One click: request the max allowed price increase"
                                      }
                                    >
                                      {submittingPriceRequestIds.has(
                                        result.raw?.id,
                                      )
                                        ? "Requesting..."
                                        : result.status === "rejected"
                                          ? "Request Increase to Relist"
                                          : "Request Price Increase"}
                                    </button>
                                  )}

                                <button
                                  className="btn-secondary btn-sm"
                                  onClick={() => startEditImages(result)}
                                  title="Reorder or swap images"
                                >
                                  Edit Images
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── Empty state ────────────────────────────────── */}
          {results.length === 0 && pageState === PAGE_STATE.IDLE && (
            <div className="results-empty">
              <p>
                {isEditor
                  ? sellerList.length === 0
                    ? "No sellers found in this company."
                    : `No SmartScan results found for ${selectedSellerName}.`
                  : "No scans yet. Upload images above to get started."}
              </p>
            </div>
          )}

          {/* ── Image edit / reorder modal ───────────────── */}
          {imageEditModalOpen && (
            <div className="reason-modal-overlay" onClick={closeImageEditModal}>
              <div
                className="reason-modal-content image-edit-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header">
                  <div className="modal-title-section">
                    <h2 className="modal-product-title">Edit Images</h2>
                    <h3 className="modal-analysis-type">
                      Drag to reorder. First image will be the thumbnail.
                    </h3>
                  </div>
                </div>
                <div className="modal-body">
                  <div className="image-edit-grid">
                    {editingImages.map((img, idx) => (
                      <div
                        key={img.id}
                        data-img-id={img.id}
                        draggable
                        onDragStart={handleModalImageDragStart(img.id)}
                        onDragOver={handleModalImageDragOver(img.id)}
                        onDragLeave={handleModalImageDragLeave}
                        onDrop={handleModalImageDrop(img.id)}
                        onDragEnd={handleModalImageDragEnd}
                        onTouchStart={handleModalTouchStart(img.id)}
                        onTouchMove={handleModalTouchMove}
                        onTouchEnd={handleModalTouchEnd}
                        className={`image-edit-card ${draggedImageId === img.id ? "preview-card--dragging" : ""} ${dragOverImageId === img.id && draggedImageId !== img.id ? "preview-card--drag-over" : ""}`}
                      >
                        {idx === 0 && (
                          <span className="thumbnail-badge">Thumbnail</span>
                        )}
                        <img
                          src={img.url}
                          alt={`Product view ${idx + 1}`}
                          loading="lazy"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = PLACEHOLDER_IMG;
                          }}
                        />
                        <p className="image-edit-order">{idx + 1}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    className="modal-btn-close modal-btn-secondary"
                    onClick={closeImageEditModal}
                    disabled={imageEditSaving}
                  >
                    Cancel
                  </button>
                  <button
                    className="modal-btn-close"
                    onClick={handleSaveImageEdits}
                    disabled={imageEditSaving}
                  >
                    {imageEditSaving ? "Saving..." : "Save Order"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Owner/admin/seller manager: review + breakdown modal ── */}
          {reviewModalOpen && reviewTarget && (
            <div
              className="reason-modal-overlay"
              onClick={!reviewSubmitting ? closeReviewModal : undefined}
            >
              <div
                className="reason-modal-content price-request-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header">
                  <div className="modal-title-section">
                    <h2 className="modal-product-title">Review Price Change</h2>
                    <h3 className="modal-analysis-type">
                      {reviewTarget.title}
                    </h3>
                  </div>
                </div>
                <div className="modal-body">
                  <div className="price-request-field">
                    <label>Requested change</label>
                    <div className="review-price-change-row">
                      <span className="price-request-card-old">
                        {reviewTarget.old_price}
                      </span>
                      <span className="price-request-card-arrow">→</span>
                      <span
                        className={`price-request-card-new price-request-card-new--${reviewTarget.change_type}`}
                      >
                        {reviewTarget.new_price}
                      </span>
                      <span
                        className={`price-request-card-pct price-request-card-pct--${reviewTarget.change_type}`}
                      >
                        {reviewTarget.change_type === "increase" ? "+" : ""}
                        {reviewTarget.change_percent}%
                      </span>
                    </div>
                  </div>

                  {reviewTarget.note && (
                    <div className="price-request-field">
                      <label>Vendor's note</label>
                      <p className="price-request-card-note">
                        "{reviewTarget.note}"
                      </p>
                    </div>
                  )}

                  {reviewBreakdownLoading ? (
                    <p className="price-request-bounds-hint">
                      Calculating impact...
                    </p>
                  ) : (
                    reviewBreakdown && (
                      <div className="review-breakdown">
                        <p className="review-breakdown-heading">
                          What happens if approved
                        </p>
                        <div className="review-breakdown-row">
                          <span>Vendor payout</span>
                          <span>
                            {reviewBreakdown.currency}
                            {reviewBreakdown.old_payout} →{" "}
                            {reviewBreakdown.currency}
                            {reviewBreakdown.new_payout}
                          </span>
                        </div>
                        <div className="review-breakdown-row">
                          <span>Recommended (customer) price</span>
                          <span>
                            {reviewBreakdown.currency}
                            {reviewBreakdown.old_price_recommended} →{" "}
                            {reviewBreakdown.currency}
                            {reviewBreakdown.new_price_recommended}
                          </span>
                        </div>
                        <p className="review-breakdown-note">
                          The recommended price moves by the same amount as the
                          payout, so this doesn't change ThriftOps' margin.
                        </p>
                        <div className="review-breakdown-delta">
                          Margin stays at {reviewBreakdown.currency}
                          {reviewBreakdown.old_margin}
                        </div>
                      </div>
                    )
                  )}

                  {reviewError && (
                    <p className="price-request-error">{reviewError}</p>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    className="modal-btn-close modal-btn-secondary"
                    onClick={() => submitReview("reject")}
                    disabled={reviewSubmitting}
                  >
                    {reviewSubmitting ? "..." : "Reject"}
                  </button>
                  <button
                    className="modal-btn-close"
                    onClick={() => submitReview("approve")}
                    disabled={reviewSubmitting || reviewBreakdownLoading}
                  >
                    {reviewSubmitting ? "Submitting..." : "Approve"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Reason modal ───────────────────────────────── */}
          {modalOpen && modalData && (
            <div className="reason-modal-overlay" onClick={closeModal}>
              <div
                className="reason-modal-content"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header">
                  <div className="modal-title-row">
                    <div className="modal-title-section">
                      <h2 className="modal-product-title">
                        {modalData.description}
                      </h2>
                      <h3 className="modal-analysis-type">
                        {modalData.type} Analysis
                      </h3>
                    </div>
                    <div className="modal-score">
                      <span className="score-label">Score</span>
                      <span
                        className={`score-value ${modalData.type === "Authentication" ? "score-auth" : "score-condition"}`}
                      >
                        {modalData.score}/10
                      </span>
                    </div>
                  </div>
                </div>
                <div className="modal-body">
                  <h4 className="reasons-heading">Analysis Details:</h4>
                  <ul className="reasons-list">
                    {modalData.reasons?.length > 0 ? (
                      modalData.reasons.map((reason, idx) => (
                        <li
                          key={idx}
                          className={`reason-item reason-${reason.status}`}
                        >
                          <span className="reason-icon">
                            {reason.status === "pass" ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                <polyline points="22 4 12 14.01 9 11.01" />
                              </svg>
                            ) : reason.status === "warning" ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                <line x1="12" y1="9" x2="12" y2="13" />
                                <line x1="12" y1="17" x2="12.01" y2="17" />
                              </svg>
                            ) : null}
                          </span>
                          <span className="reason-text">{reason.text}</span>
                        </li>
                      ))
                    ) : (
                      <li className="reason-item reason-pass">
                        <span className="reason-icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="16" x2="12" y2="12" />
                            <line x1="12" y1="8" x2="12.01" y2="8" />
                          </svg>
                        </span>
                        <span className="reason-text">
                          No detailed analysis available. Process the images
                          again to generate details.
                        </span>
                      </li>
                    )}
                  </ul>
                </div>
                <div className="modal-footer">
                  <button className="modal-btn-close" onClick={closeModal}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SorterPage;
